// Low-poly pieces of a tile's interior (PRD section 14.3): belts as dark raised bands with a light curb
// and white arrows, machines as 2 × 2 blocks with a blue or orange roof and an element that says what
// they do, the items that ride the belts, and the doors. Every piece is baked into a GeometryBatch.
import { BoxGeometry, CylinderGeometry, DodecahedronGeometry, Shape, ShapeGeometry, type BufferGeometry } from 'three';
import type { MachineDef } from '../sim/data';
import type { Side } from '../sim/geometry';
import { GeometryBatch } from './batch';
import { C } from './palette';
import { box, house } from './prefabs';

/** Height of a belt's top above the ground: items ride on it. */
export const BELT_TOP = 0.115;
/** Width of the dark band of a belt, in cells. */
const BELT_WIDTH = 0.56;

export const SIDE_VECTOR: Readonly<Record<Side, readonly [number, number]>> = { N: [0, -1], E: [1, 0], S: [0, 1], W: [-1, 0] };

/** Turn around the vertical axis that makes the +x axis point towards `side` (E is 0). */
export const sideAngle = (side: Side): number => {
  const [dx, dz] = SIDE_VECTOR[side];
  return Math.atan2(-dz, dx);
};

/** A flat white chevron pointing along +x, lying on the ground. */
export function chevronGeometry(size = 1): BufferGeometry {
  const s = new Shape();
  s.moveTo(-0.05 * size, -0.14 * size);
  s.lineTo(0.07 * size, 0);
  s.lineTo(-0.05 * size, 0.14 * size);
  s.lineTo(-0.12 * size, 0.14 * size);
  s.lineTo(0, 0);
  s.lineTo(-0.12 * size, -0.14 * size);
  const geo = new ShapeGeometry(s);
  geo.rotateX(-Math.PI / 2);
  return geo;
}

const CHEVRON = chevronGeometry();

/**
 * One belt cell centred at (x, z): a square in the middle and a half band towards each side it
 * connects (where it takes material in and the side it hands it on through), with two arrows.
 */
export function beltPiece(b: GeometryBatch, x: number, z: number, exit: Side, inputs: readonly Side[]): void {
  const parts: [number, number, number, number][] = [[0, 0, BELT_WIDTH, BELT_WIDTH]];
  for (const side of new Set([...inputs, exit])) {
    const [vx, vz] = SIDE_VECTOR[side];
    parts.push([vx * 0.25, vz * 0.25, vx ? 0.5 : BELT_WIDTH, vz ? 0.5 : BELT_WIDTH]);
  }
  for (const [dx, dz, w, l] of parts) {
    b.add(box(w + 0.06, 0.1, l + 0.06), C.beltCurb, { position: [x + dx, 0.05, z + dz] });
    b.add(box(w, 0.11, l), C.belt, { position: [x + dx, 0.055, z + dz] });
  }
  const [vx, vz] = SIDE_VECTOR[exit];
  for (const k of [-0.18, 0.17]) {
    b.add(CHEVRON, C.beltArrow, { position: [x + vx * k, BELT_TOP + 0.003, z + vz * k], rotation: [0, sideAngle(exit), 0] }, { smooth: true });
  }
}

/** A short belt across the rim, from a door to the edge of the board, its arrows pointing the way material goes. */
export function doorBelt(b: GeometryBatch, x: number, z: number, side: Side, length: number, inward: boolean): void {
  const [vx, vz] = SIDE_VECTOR[side];
  const w = vx ? length : BELT_WIDTH;
  const l = vz ? length : BELT_WIDTH;
  b.add(box(w + 0.06, 0.1, l + 0.06), C.beltCurb, { position: [x, 0.05, z] });
  b.add(box(w, 0.11, l), C.belt, { position: [x, 0.055, z] });
  const towards: Side = inward ? ({ N: 'S', S: 'N', E: 'W', W: 'E' } as const)[side] : side;
  b.add(CHEVRON, C.beltArrow, { position: [x, BELT_TOP + 0.003, z], rotation: [0, sideAngle(towards), 0] }, { smooth: true });
}

/** The marks where material goes into a machine (blue) and comes out (orange), on its footprint's edge. */
function socketMark(b: GeometryBatch, x: number, z: number, side: Side, kind: 'in' | 'out'): void {
  const [vx, vz] = SIDE_VECTOR[side];
  const color = kind === 'in' ? C.portIn : C.portOut;
  const glow = { emissive: color, emissiveIntensity: 0.3 };
  const px = x + vx * 0.42;
  const pz = z + vz * 0.42;
  b.add(box(vx ? 0.14 : 0.5, 0.16, vz ? 0.14 : 0.5), color, { position: [px, 0.08 + 0.08, pz] }, glow);
  const towards: Side = kind === 'in' ? ({ N: 'S', S: 'N', E: 'W', W: 'E' } as const)[side] : side;
  b.add(CHEVRON, '#ffffff', { position: [px, 0.245, pz], rotation: [0, sideAngle(towards), 0], scale: 0.8 }, { smooth: true, emissive: '#ffffff', emissiveIntensity: 0.2 });
}

/** A furnace: a stone crucible glowing with molten iron, a tall chimney and a workshop with an orange roof. */
function furnace(b: GeometryBatch): void {
  house(b, 0, 0.08, 0.42, 1.6, 0.78, 0.34, C.roofOrange, { door: true });
  b.add(new CylinderGeometry(0.34, 0.4, 0.4, 10), C.stone, { position: [-0.38, 0.28, -0.4] });
  b.add(new CylinderGeometry(0.37, 0.37, 0.05, 10), '#7f7b74', { position: [-0.38, 0.49, -0.4] });
  b.add(new CylinderGeometry(0.27, 0.27, 0.03, 10), '#ffb347', { position: [-0.38, 0.51, -0.4] }, { emissive: '#ff7a00', emissiveIntensity: 1 });
  b.add(new CylinderGeometry(0.11, 0.14, 1.05, 8), C.stone, { position: [0.52, 0.6, -0.42] });
  b.add(new CylinderGeometry(0.13, 0.13, 0.06, 8), '#7f7b74', { position: [0.52, 1.13, -0.42] });
}

/** A press: a steel frame with its ram over the anvil, a big gear, and a workshop with a blue roof. */
function press(b: GeometryBatch): void {
  house(b, 0, 0.08, 0.42, 1.6, 0.78, 0.34, C.roofBlue, { door: true });
  b.add(box(0.7, 0.08, 0.5), C.dark, { position: [-0.25, 0.12, -0.42] });
  for (const px of [-0.62, 0.12]) b.add(box(0.1, 0.78, 0.12), C.metal, { position: [px, 0.47, -0.42] });
  b.add(box(0.9, 0.12, 0.2), C.metal, { position: [-0.25, 0.86, -0.42] });
  b.add(box(0.26, 0.34, 0.26), '#6b7584', { position: [-0.25, 0.6, -0.42] });
  gearPiece(b, 0.55, 0.36, -0.42, 0.3, '#b8c2cf');
  b.add(new CylinderGeometry(0.06, 0.08, 0.28, 6), C.metal, { position: [0.55, 0.22, -0.42] });
}

/** A flat gear: a disc with teeth and a dark hub. */
export function gearPiece(b: GeometryBatch, x: number, y: number, z: number, r: number, color: string): void {
  const h = r * 0.3;
  b.add(new CylinderGeometry(r * 0.82, r * 0.82, h, 12), color, { position: [x, y, z] });
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    b.add(box(r * 0.32, h, r * 0.3), color, { position: [x + Math.cos(a) * r * 0.86, y, z + Math.sin(a) * r * 0.86], rotation: [0, -a, 0] });
  }
  b.add(new CylinderGeometry(r * 0.25, r * 0.25, h * 1.2, 8), '#4c5563', { position: [x, y, z] });
}

/** Any other machine: a workshop with a roof, until it gets its own model. */
function workshop(b: GeometryBatch, w: number, d: number): void {
  house(b, 0, 0.08, 0, w - 0.4, d - 0.4, 0.4, C.roofBlue, { door: true });
}

/**
 * A machine of `def` in its own frame: the footprint before rotation is centred on the origin, with
 * cells along +x (east) and +z (south). The caller places and turns it.
 */
export function machinePiece(b: GeometryBatch, def: MachineDef): void {
  const [w, d] = def.size;
  b.add(box(w - 0.12, 0.08, d - 0.12), C.timber, { position: [0, 0.04, 0] });
  if (def.id === 'furnace') furnace(b);
  else if (def.id === 'press') press(b);
  else workshop(b, w, d);
  const cellCentre = ([c, r]: readonly [number, number]): [number, number] => [c + 0.5 - w / 2, r + 0.5 - d / 2];
  socketMark(b, ...cellCentre(def.in.cell), def.in.side, 'in');
  socketMark(b, ...cellCentre(def.out.cell), def.out.side, 'out');
}

/** Geometry of an item riding a belt, centred on its own origin and resting on y = 0, facing +x. */
export function itemGeometry(item: string, color: string): BufferGeometry {
  const b = new GeometryBatch();
  switch (item) {
    case 'iron_ore':
      b.add(new DodecahedronGeometry(0.15, 0), C.ore, { position: [0, 0.11, 0], scale: [1.15, 0.8, 1] });
      b.add(new DodecahedronGeometry(0.075, 0), '#c98a6a', { position: [0.06, 0.2, -0.05] });
      break;
    case 'iron_plate':
      b.add(box(0.36, 0.06, 0.28), C.iron, { position: [0, 0.04, 0] });
      b.add(box(0.33, 0.055, 0.25), '#c4cedb', { position: [0.015, 0.095, 0.01] });
      break;
    case 'gear':
      gearPiece(b, 0, 0.06, 0, 0.19, '#b8c2cf');
      break;
    default:
      b.add(new BoxGeometry(0.26, 0.16, 0.26), color, { position: [0, 0.08, 0] });
  }
  return b.build();
}
